import { useState, useEffect } from "react";
import ConsoleLayout from "@/components/ConsoleLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { usersApi } from "@/lib/api";
import { adaptUsageRecord } from "@/lib/adapters";
import type { AdaptedUsageLog } from "@/lib/types";
import { UsageLogRow } from "@/components/UsageLogRow";
import { Download, ChevronLeft, ChevronRight } from "lucide-react";
import { exportToCsv } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { DatePicker } from "@/components/ui/date-picker";

// Local calendar date (YYYY-MM-DD) for the default "today 00:00 → now" range.
function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Personal call-log page. Always scoped to the caller via /users/me/usage,
// regardless of team role — every user sees this view. Upstream / provider /
// per-dimension pricing are stripped server-side; only the ScienceLLM product
// key is exposed.
//
// Filters are draft/applied split: editing an input does NOT fetch. Fetch only
// happens on an explicit Query click (bumps reloadNonce) or on pagination
// (page change). This keeps requests predictable — one per user action.
export default function ExperimentLogs() {
  const { t, language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const [logs, setLogs] = useState<AdaptedUsageLog[]>([]);
  const [loading, setLoading] = useState(true);

  // Draft inputs — edited freely, only committed to applied* on Query.
  const [model, setModel] = useState("");
  const [requestId, setRequestId] = useState("");
  const [status, setStatus] = useState("");
  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());

  // Applied filters — the ONLY values fetchLogs reads. Seeded with the same
  // today defaults so the first load queries today 00:00 → now.
  const [appliedModel, setAppliedModel] = useState("");
  const [appliedRequestId, setAppliedRequestId] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState(todayStr());
  const [appliedEndDate, setAppliedEndDate] = useState(todayStr());

  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [exporting, setExporting] = useState(false);
  const PAGE_SIZE = 50;

  const buildParams = (limit: number, offset: number) => ({
    limit,
    offset,
    model: appliedModel || undefined,
    request_id: appliedRequestId || undefined,
    status: appliedStatus || undefined,
    after: appliedStartDate ? new Date(appliedStartDate + "T00:00:00").toISOString() : undefined,
    before: appliedEndDate ? new Date(appliedEndDate + "T23:59:59").toISOString() : undefined,
  });

  const fetchLogs = async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const usage = await usersApi.getMyUsage(buildParams(PAGE_SIZE, page * PAGE_SIZE));
      setLogs(usage.records.map((r) => adaptUsageRecord(r, usage.api_key_names || {}, language)));
      setTotal(usage.total);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  // Fetch ONLY on auth ready, page change, or an explicit query (reloadNonce).
  // Filter edits are intentionally absent from the deps.
  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, page, reloadNonce]);

  const runQuery = () => {
    setAppliedModel(model);
    setAppliedRequestId(requestId);
    setAppliedStatus(status);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setPage(0);
    setReloadNonce((n) => n + 1); // force a fetch even if nothing changed
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleExportCsv = async () => {
    if (total === 0) return;
    setExporting(true);
    try {
      const allUsage = await usersApi.getMyUsage(buildParams(total, 0));
      const allLogs = allUsage.records.map((r) => adaptUsageRecord(r, allUsage.api_key_names || {}, language));
      exportToCsv(
        `call-logs-${new Date().toISOString().slice(0, 10)}.csv`,
        ["Request ID", "Model", "Type", "Status", "Timestamp", "Input Tokens", "Output Tokens", "Cache Read Tokens", "Cache Write Tokens", "Total Tokens", "Latency (ms)", "Cost (USD)", "API Key", "Error"],
        allLogs.map((l) => [l.requestId, l.model, l.type, l.status, l.timestamp, String(l.inputTokens || 0), String(l.outputTokens || 0), String(l.cacheReadTokens || 0), String(l.cacheWriteTokens || 0), String(l.tokens), String(l.latency), l.cost, l.keyName, l.errorMessage || ""])
      );
    } catch {
      // ignore
    } finally {
      setExporting(false);
    }
  };

  return (
    <ConsoleLayout
      title={t("logs.title")}
      subtitle={t("logs.subtitle")}
      actions={
        <Button size="sm" variant="ghost" className="gap-1.5 text-xs h-8 text-slate-400 border border-white/10" onClick={handleExportCsv} disabled={total === 0 || exporting}>
          <Download size={12} />
          {exporting ? "..." : t("logs.exportCsv")}
        </Button>
      }
    >
      <div className="p-6 space-y-5">
        {/* Filters: Time — Model — RequestId — Status — Query */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <DatePicker value={startDate} onChange={setStartDate} />
            <span className="text-xs text-slate-600">—</span>
            <DatePicker value={endDate} onChange={setEndDate} />
          </div>
          <Input
            placeholder={t("logs.searchModelPlaceholder")}
            value={model}
            onChange={(e) => setModel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runQuery(); }}
            className="flex-1 min-w-[160px] max-w-[220px] bg-white/5 border-white/10 text-sm h-9 text-white placeholder:text-slate-600"
          />
          <Input
            placeholder={t("logs.searchRequestIdPlaceholder")}
            value={requestId}
            onChange={(e) => setRequestId(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runQuery(); }}
            maxLength={26}
            className="flex-1 min-w-[160px] max-w-[240px] bg-white/5 border-white/10 text-sm h-9 text-white placeholder:text-slate-600 font-mono"
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white h-9 focus:outline-none focus:border-blue-500/50"
          >
            <option value="">{t("logs.statusAll")}</option>
            <option value="success">{t("logs.statusSuccess")}</option>
            <option value="failed">{t("logs.statusFailed")}</option>
            <option value="running">{t("logs.statusRunning")}</option>
          </select>
          <Button size="sm" variant="ghost" onClick={runQuery} className="h-9 px-4 text-xs text-slate-300 border border-white/10">
            {t("logs.applyFilters")}
          </Button>
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-slate-500">{total} {t("logs.records")}</span>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-30"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="text-xs text-slate-400">{page + 1} / {totalPages}</span>
                <button
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                  className="p-1 rounded text-slate-500 hover:text-white disabled:opacity-30"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Logs Table */}
        <div className="sci-card overflow-hidden">
          <div className="px-5 py-3 border-b border-white/8 grid grid-cols-[24px_1fr_120px_80px_20px] gap-4 text-[10px] text-slate-600 uppercase tracking-wider items-center">
            <div />
            <div>{t("logs.modelRequest")}</div>
            <div className="hidden sm:block text-right">{t("logs.usage")}</div>
            <div className="text-right">{t("logs.cost")}</div>
            <div />
          </div>
          {logs.length > 0 ? (
            logs.map((log) => <UsageLogRow key={log.id} log={log} />)
          ) : (
            <div className="py-16 text-center text-slate-500 text-sm">
              {loading ? "Loading..." : t("logs.noLogs")}
            </div>
          )}
        </div>
      </div>
    </ConsoleLayout>
  );
}
